import { gql } from "@apollo/client";
import { AccountFragment } from "fragments/account";

export const SiteFragment = gql`
  fragment SiteFragment on Site {
    id
    siteId
    orgId
    title

    status

    createdAt
    updatedAt
  }
`;

// The document shell, which only the screens that resolve ONE site ask for.
//
// Split out rather than added to SiteFragment because a configuration carries a
// body template and three inline blocks — a page of text each, easily — and the
// list query would then pull all of it for twenty-five sites to render a table
// of names and dates. The archived project split the same two shapes at the
// same seam and called them SiteFragment and SiteFullFragment.
//
// The edit mutation selects it too, so a save writes the new shell straight
// into the cache entry the preview reads.
export const SiteFullFragment = gql`
  fragment SiteFullFragment on Site {
    ...SiteFragment
    config
    # Beside \`config\` and not on SiteFragment above, on the same size argument
    # — but mostly because these have the same two readers it does: the site's
    # own screen edits them, and BOTH composers read \`defaultLocale\` through
    # \`localeOf\` to decide what language a page with none of its own is in.
    # The sites list needs neither.
    locales {
      code
      prefix
      label
    }
    defaultLocale
    # The catalog. On the full fragment beside the locales because both
    # composers read it: contextOf overlays it on the site's values, so a
    # preview that loaded without it would show English on a Spanish page.
    translations {
      locale
      name
      value
    }
    # Where a URL nothing is published at sends the visitor. On the full
    # fragment for the size argument alone: the site's own screen is its one
    # reader, and the list does not need it.
    redirects {
      from
      to
      prefix
      permanent
    }
  }
  ${SiteFragment}
`;

export const get = gql`
  query SiteGet($siteId: String!) {
    viewer {
      id
      account {
        ...AccountFragment
        sites {
          id
          get(siteId: $siteId) {
            ...SiteFullFragment
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${SiteFullFragment}
`;

export const list = gql`
  query SiteList($input: ListInput) {
    viewer {
      id
      account {
        id
        sites {
          id
          list(input: $input) {
            id
            last
            items {
              ...SiteFragment
            }
          }
        }
      }
    }
  }
  ${SiteFragment}
`;

export const create = gql`
  mutation SiteNew($input: SiteInputCreate!) {
    viewer {
      account {
        sites {
          create(input: $input) {
            ...SiteFragment
          }
        }
      }
    }
  }
  ${SiteFragment}
`;

export const edit = gql`
  mutation SiteEdit($siteId: String!, $input: SiteInputUpdate!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            update(input: $input) {
              ...SiteFullFragment
            }
          }
        }
      }
    }
  }
  ${SiteFullFragment}
`;

export const remove = gql`
  mutation SiteRemove($siteId: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            remove
          }
        }
      }
    }
  }
`;

// The literal below must equal what SiteQueries.id resolves to on the API side
// (api/code/src/schema/account/site). If the two drift, a create succeeds and
// every list screen keeps serving the pre-create site with no error anywhere.
// api/code/test/schema.test.js pins the other half.
export const update = ({ siteId }) => (cache) => {

  Object.keys(cache.data.data).forEach((key) => {
    if (key.includes(`sites/queries`)) {
      cache.data.delete(key);
    }
  });
};
